import { useCallback, useEffect, useState } from "react";
import { ROTULO_SECAO } from "../config";
import type { Row } from "../types";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { IconCheck, IconClose, IconRefresh } from "../icons";

/**
 * APROVAÇÃO DE RELATÓRIO — a fila do segundo olhar.
 *
 * Aqui alguém que NÃO fez a análise confere o diagnóstico e libera (ou devolve).
 * Só com aprovação válida o laudo pode ser despachado ao Relatórios.
 *
 * A tela mostra o diagnóstico do jeito que ele foi gerado — parâmetro, valor
 * medido, faixa e veredito — porque aprovar sem ver o que se aprova não é
 * conferência, é carimbo. Reprovar exige motivo escrito: sem ele o analista
 * não sabe o que refazer.
 */
type Aviso = { tipo: "ok" | "erro"; texto: string } | null;

export default function AprovacaoView() {
  const [rows, setRows] = useState<Row[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [aberto, setAberto] = useState<Row | null>(null);
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [soPendentes, setSoPendentes] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setRows(await api.list("aprovacoes_laudo"));
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function decidir(pedido: Row, aprovar: boolean) {
    if (!aprovar && !motivo.trim()) {
      setAviso({ tipo: "erro", texto: "Escreva o motivo — é o que diz ao analista o que refazer." });
      return;
    }
    setOcupado(true);
    setAviso(null);
    try {
      const r = aprovar
        ? await api.aprovacoes.aprovar(Number(pedido.id))
        : await api.aprovacoes.reprovar(Number(pedido.id), motivo.trim());
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Pronto." });
      setAberto(null);
      setMotivo("");
      await carregar();
    } catch (e) {
      setAviso({ tipo: "erro", texto: e instanceof ApiError ? e.message : "Erro ao registrar a decisão." });
    } finally {
      setOcupado(false);
    }
  }

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  const lista = soPendentes ? rows.filter((r) => r.status === "Pendente") : rows;
  const pendentes = rows.filter((r) => r.status === "Pendente").length;

  return (
    <div className="aprov">
      <div className="aprov-topo">
        <div>
          <strong>{pendentes}</strong> laudo(s) aguardando conferência
        </div>
        <label className="aprov-filtro">
          <input type="checkbox" checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} />
          mostrar só os pendentes
        </label>
        <button className="btn-secondary" onClick={carregar} disabled={carregando}>
          <IconRefresh size={15} /> Atualizar
        </button>
      </div>

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      {carregando ? (
        <div className="empty">Carregando…</div>
      ) : lista.length === 0 ? (
        <div className="empty">
          {soPendentes ? "Nenhum laudo aguardando conferência." : "Nenhuma aprovação registrada ainda."}
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>OS</th>
              <th>Cliente</th>
              <th>Equipamento</th>
              <th>Situação</th>
              <th>Solicitado por</th>
              <th className="col-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.os_numero ?? "—"}</strong></td>
                <td>{r.cliente ?? "—"}</td>
                <td>{r.equipamento ?? "—"}</td>
                <td>
                  <span className={`pill pill-${r.status === "Aprovado" ? "green" : r.status === "Reprovado" ? "red" : "amber"}`}>
                    {r.status}
                  </span>
                </td>
                <td>{r.solicitado_por ?? "—"}</td>
                <td className="col-actions">
                  {r.status === "Pendente" ? (
                    <button className="btn-mini" onClick={() => { setAberto(r); setMotivo(""); setAviso(null); }}>
                      Conferir
                    </button>
                  ) : (
                    <span className="muted" title={r.motivo ?? ""}>
                      {r.avaliado_por ? `por ${r.avaliado_por}` : "—"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {aberto && <PainelConferencia pedido={aberto} motivo={motivo} setMotivo={setMotivo}
        ocupado={ocupado} onFechar={() => setAberto(null)} onDecidir={decidir} />}
    </div>
  );
}

/**
 * Painel da conferência. Componente separado (e no nível de cima do arquivo) de
 * propósito: declarado DENTRO do outro, ele remontaria a cada tecla digitada no
 * motivo e o campo perderia o foco.
 */
function PainelConferencia({
  pedido, motivo, setMotivo, ocupado, onFechar, onDecidir,
}: {
  pedido: Row;
  motivo: string;
  setMotivo: (v: string) => void;
  ocupado: boolean;
  onFechar: () => void;
  onDecidir: (p: Row, aprovar: boolean) => void;
}) {
  const diag = typeof pedido.snapshot === "string" ? safeJson(pedido.snapshot) : pedido.snapshot;

  return (
    <div className="modal-overlay" onMouseDown={onFechar}>
      <div className="modal-card modal-wide" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Conferir o laudo</h3>
            <p className="muted">
              OS {String(pedido.os_numero ?? "—")} · {String(pedido.cliente ?? "—")}
              {pedido.solicitado_por ? ` · pedido por ${pedido.solicitado_por}` : ""}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onFechar}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="aprov-corpo">
          {/*
            O PARECER ESCRITO NA BANCADA — o que se confere quando o pedido vem
            do fluxo da OS (diagnóstico, recomendação, reamostragem e status).
            Antes o painel só sabia desenhar o diagnóstico do motor de limites e
            dizia "o pedido não trouxe o diagnóstico" — aprovar assim é carimbar.
          */}
          {diag?.parecer && (
            <div className="diag-secao">
              <div className="diag-secao-head">
                <strong>Parecer do laboratório</strong>
                <span className="muted">{diag.parecer.status ?? "sem status"}</span>
              </div>
              <p className="diag-resumo">
                <strong>Diagnóstico:</strong> {diag.parecer.diagnostico || "—"}
              </p>
              {diag.parecer.recomendacao && (
                <p className="diag-resumo">
                  <strong>Recomendação:</strong> {diag.parecer.recomendacao}
                </p>
              )}
              {diag.parecer.reamostragem && (
                <p className="diag-resumo">
                  <strong>Reamostragem:</strong> {diag.parecer.reamostragem}
                </p>
              )}
              {diag.parecer.finalizado_em && (
                <p className="muted">
                  Finalizado em {new Date(String(diag.parecer.finalizado_em)).toLocaleString("pt-BR")}
                  {diag.parecer.finalizado_por ? ` por ${diag.parecer.finalizado_por}` : ""} — abaixo,
                  os valores em que ele se baseia.
                </p>
              )}
            </div>
          )}

          {!diag?.secoes?.length ? (
            diag?.parecer ? (
              <div className="empty">
                Nenhum valor lançado nas seções desta amostra — confira o parecer acima.
              </div>
            ) : (
              <div className="empty">O pedido não trouxe o diagnóstico — peça ao analista para gerar de novo.</div>
            )
          ) : (
            <>
              <div className="diag-msg">
                Veredito geral: <strong>{diag.veredito ?? "Indeterminado"}</strong>
                {diag.incompleto && " — PARCIAL: há parâmetros medidos sem limite cadastrado."}
                {diag.origem === "diagnostico-amostra" &&
                  " — os valores abaixo são os lançados na bancada; a faixa da norma só aparece no diagnóstico gerado pelo motor de limites."}
              </div>
              {diag.secoes.map((sec: any) => (
                <div className="diag-secao" key={sec.secao}>
                  <div className="diag-secao-head">
                    <strong>{ROTULO_SECAO[sec.secao] ?? sec.secao}</strong>
                    <span className="muted">{sec.veredito ?? "Indeterminado"}</span>
                  </div>
                  <p className="diag-resumo">{sec.resumo}</p>
                  <table className="data-table diag-tabela">
                    <thead>
                      <tr><th>Parâmetro</th><th>Medido</th><th>Faixa</th><th>Veredito</th></tr>
                    </thead>
                    <tbody>
                      {sec.parametros
                        .filter((p: any) => p.situacao !== "sem-valor")
                        .map((p: any) => (
                          <tr key={p.parametro}>
                            <td>{p.rotulo}</td>
                            <td>{p.valorInformado ?? "—"}{p.unidade ? ` ${p.unidade}` : ""}</td>
                            <td className="diag-faixa">{p.faixa ? `${p.faixa} (${p.norma})` : "—"}</td>
                            <td>{p.veredito ?? "—"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}

          <label className="aprov-motivo">
            <span>Motivo (obrigatório para reprovar)</span>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              placeholder="ex.: refazer o ensaio de rigidez — valor incompatível com o histórico"
            />
          </label>
        </div>

        <div className="modal-foot">
          <button className="btn-secondary" onClick={onFechar} disabled={ocupado}>Cancelar</button>
          <button className="btn-secondary" onClick={() => onDecidir(pedido, false)} disabled={ocupado}>
            <IconClose size={15} /> Reprovar
          </button>
          <button className="btn-primary" onClick={() => onDecidir(pedido, true)} disabled={ocupado}>
            <IconCheck size={15} /> Aprovar
          </button>
        </div>
      </div>
    </div>
  );
}

function safeJson(v: string): any {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}
